import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import { useSelector, shallowEqual } from 'react-redux';
import { number, string, bool } from 'prop-types';
import InputField from '#component/form/input';
import RHFLegacyField from '#component/form/rhf/rhf-legacy-field';
import { normalizeNumberField } from '#component/project/form/packageTypes/package-item/helper';
import { FieldsGroupCnt } from '#component/project/form/packageTypes/package-item/styles';
import { convertLength, convertWeight, getLengthUnit, getWeightUnit } from '#helper';

const dimensionsFields = [
  { name: 'length', label: 'Length' },
  { name: 'width', label: 'Width' },
  { name: 'height', label: 'Height' }
];

/**
 * Renders dimensions and weight fields.
 * @returns {React.FC}
 */

const DimensionsWeightFields = props => {
  const { member, tareWeight = null, disabled, forceError } = props;
  const { setValue } = useFormContext();
  const [touched, setTouched] = useState({});
  const measurementSystem = useSelector(s => s.account?.measurementSystem, shallowEqual);

  const change = (name, value) => {
    setValue(name, value, { shouldDirty: true, shouldValidate: true });
  };

  const handleDimensionsChange = (event, newValue, previousValue, name) => {
    if (!touched[name]) {
      setTouched(t => ({ ...t, [name]: true }));
    }
  };

  const handleDimensionsBlur = (event, newValue, previousValue, name) => {
    event.preventDefault();
    if (newValue) change(name, convertLength(newValue, measurementSystem, 'metric'));
    setTouched(t => ({ ...t, [name]: false }));
  };

  const dimensionsFormat = name => (!touched[name] ?
    value =>
      (value ? `${Math.round(100 * convertLength(value, 'metric', measurementSystem)) / 100}` : '') :
    undefined);

  const handleWeightChange = (event, newValue, previousValue, name) => {
    if (!touched[name]) {
      setTouched(t => ({ ...t, [name]: true }));
    }
  };

  const handleWeightBlur = (event, newValue, previousValue, name) => {
    event.preventDefault();
    if (newValue) change(name, convertWeight(newValue, measurementSystem, 'metric'));
    setTouched(t => ({ ...t, [name]: false }));
  };

  const weightFormat = name => (!touched[name] ?
    value =>
      (value ? `${Math.round(100 * convertWeight(value, 'metric', measurementSystem)) / 100}` : '') :
    undefined);

  return (
    <FieldsGroupCnt>
      <Grid container spacing={2}>
        {dimensionsFields.map(field => (
          <Grid item xs={12} sm={6} md={3} key={field.name}>
            <RHFLegacyField
              name={`${member}.${field.name}`}
              component={InputField}
              label={field.label}
              variant="outlined"
              fullWidth
              onBlur={handleDimensionsBlur}
              format={dimensionsFormat(`${member}.${field.name}`)}
              onChange={handleDimensionsChange}
              normalize={normalizeNumberField}
              InputProps={{
                endAdornment: <InputAdornment position="end">{getLengthUnit(measurementSystem)}</InputAdornment>
              }}
              disabled={disabled}
              forceError={forceError}
            />
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={3}>
          <RHFLegacyField
            name={`${member}.tareWeight`}
            component={InputField}
            label="Total weight"
            variant="outlined"
            fullWidth
            onBlur={handleWeightBlur}
            format={weightFormat(`${member}.tareWeight`)}
            onChange={handleWeightChange}
            normalize={normalizeNumberField}
            info={tareWeight ? `Tare ${Math.round(100 * convertWeight(tareWeight, 'metric', measurementSystem)) / 100} ${getWeightUnit(measurementSystem)}` : null}
            InputProps={{
              endAdornment: <InputAdornment position="end">{getWeightUnit(measurementSystem)}</InputAdornment>
            }}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </FieldsGroupCnt>
  );
};

export default DimensionsWeightFields;

DimensionsWeightFields.propTypes = {
  member: string.isRequired,
  tareWeight: number,
  disabled: bool
};
